import { useMemo } from 'react';

import { toast } from 'react-toastify';

import { useFilter } from '@features/table-filter';

import {
  useCopyPolicyMutation,
  useCreatePolicyMutation,
  useDeletePolicyMutation,
  useGetPoliciesQuery,
  useUpdatePolicyMutation,
  useUpdateRulePriorityMutation,
} from '../api';
import { IPolicyFilter } from '../model/types';

export const usePolicies = () => {
  const filter = useFilter<IPolicyFilter>({ disableUrlFilter: true });
  const { data, isLoading, isError, error, isFetching, refetch } = useGetPoliciesQuery(
    filter.currentFilter.toString(),
  );
  const [deletePolicy] = useDeletePolicyMutation();
  const [updatePolicy] = useUpdatePolicyMutation();
  const [createPolicy] = useCreatePolicyMutation();
  const [copyPolicy] = useCopyPolicyMutation();
  const [updateRulePriority] = useUpdateRulePriorityMutation();

  const orderedPolicies = useMemo(() => {
    const normalized = (data?.data ?? []).map(policy => ({
      ...policy,
      conditions: policy.conditions ?? [],
      rules: policy.rules ?? [],
    }));
    return [...normalized].sort((a, b) => a.priority - b.priority);
  }, [data?.data]);

  const clonePolicy = async (id: string) => {
    await copyPolicy({ id }).unwrap();
    refetch();
  };

  const reorderRules = async (policyId: string, ruleIds: string[]) => {
    if (!ruleIds.length) return;
    try {
      await Promise.all(
        ruleIds.map((id, idx) => updateRulePriority({ id, priority: idx + 1 }).unwrap()),
      );
      toast.success('Rules priority updated');
    } catch (error) {
      toast.error('Failed to update rules priority');
      throw error;
    }
  };

  const reorderPolicies = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id, idx) => updatePolicy({ id, priority: idx + 1 }).unwrap()));
      toast.success('Policies priority updated');
      refetch();
    } catch (error) {
      toast.error('Failed to update policies priority');
      throw error;
    }
  };

  return {
    policies: orderedPolicies,
    meta: data?.meta,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
    filter,
    createPolicy,
    updatePolicy,
    deletePolicy,
    clonePolicy,
    reorderRules,
    reorderPolicies,
  };
};
